import {
  css, keyframes,
} from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import {
  faFaceGrinWide, faFaceSunglasses, faLockAlt,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useEffect, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { PasswordStrengthIndicatorComponent } from '~/_shared/components/passwordStrengthIndicator/passwordStrengthIndicator.component';
import { TermsAndPrivacyNoticeComponent } from '~/_shared/components/termsAndPrivacyNotice/termsAndPrivacyNotice.component';
import {
  Breakpoint, mq,
} from '~/_shared/style.mixins';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { FormField } from '~/authentication/authenticationFormField.types';
import { SsoButtonsComponent } from '~/authentication/loginPage/sso/ssoButtons.component';
import { ITEMS_GAP } from '~/authentication/registerPage/form/registerForm.constants';
import type { UserRegisterErrors } from '~/store/userData/repository/userData.repository';
import { authorizationPagesErrorStyle } from '../../authenticationPages.styles';
import { AuthorizationSubmitButtonComponent } from '../../submitButton/authenticationSubmitButton.component';
import { RegisterFormFooter } from './registerFormFooter.component';

const EXPAND_ANIMATION_DURATION_MS = 200;

const privacyTextStyle = css({
  fontSize: 16,
  marginLeft: 8,
});

const privacyNoticeStyle = css({
  cursor: 'default',
  fontSize: 14,
});

// grows the grid row from 0fr to its content height, while the negative margin
// cancels out the stack gap so the collapsed state keeps its original spacing
const detailsExpandKeyframes = keyframes({
  from: {
    gridTemplateRows: '0fr',
    marginTop: -ITEMS_GAP,
  },
  to: {
    gridTemplateRows: '1fr',
    marginTop: 0,
  },
});

const detailsFadeInKeyframes = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const detailsWrapperStyle = css({
  display: 'grid',
  gridTemplateRows: '1fr',
  animation: `${detailsExpandKeyframes} ${EXPAND_ANIMATION_DURATION_MS}ms ease-out`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

const detailsContentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: ITEMS_GAP,
  minHeight: 0,
  overflow: 'hidden',
  animation: `${detailsFadeInKeyframes} ${EXPAND_ANIMATION_DURATION_MS}ms ease-out`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    overflow: 'visible',
  },
});

// input focus glow must not be clipped once the expansion is over
const detailsContentExpandedStyle = css({
  overflow: 'visible',
});

const rowWithColumnsStyle = css({
  [mq(Breakpoint.mid)]: {
    flexDirection: 'row',
    gap: 8,
  },
});

const columnStyle = css({
  flex: 1,
  minWidth: 0,
});

const orDividerWrapperStyle = css({
  cursor: 'default',
  display: 'flex',
  alignItems: 'center',
});

const orDividerLineStyle = (theme: Theme) => css({
  flex: 1,
  height: 1,
  backgroundColor: theme.lineColors.basePrimary,
});

const orDividerTextStyle = (theme: Theme) => css({
  padding: '0 12px',
  fontSize: 12,
  color: theme.textColors.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
});

const passwordStrengthWrapperStyle = css({
  fontSize: 14,
  width: '80%',
  margin: '0 auto',
});

const alertStyle = css({
  marginTop: 10,
});

type RegisterFormViewProps = Readonly<{
  areDetailsVisible: boolean;
  email: FormField;
  firstName: FormField;
  lastName: FormField;
  password: FormField;
  apiError: Readonly<{ message?: string; errors?: UserRegisterErrors | null; onClose: () => void }>;
  isLoading: boolean;
  requireTOS: boolean;
  sso: Readonly<{ onGoogleLogin: () => void; onMicrosoftLogin: () => void }>;
  terms: Readonly<{ accepted: boolean; showError: boolean; onChange: (checked: boolean) => void }>;
  onEmailFocus: () => void;
}>;

export const RegisterFormView: FC<RegisterFormViewProps> = (props) => {
  const [isExpandFinished, setIsExpandFinished] = useState(false);
  const [t] = useTranslation();

  useEffect(() => {
    if (!props.areDetailsVisible) {
      setIsExpandFinished(false);
    }
  }, [props.areDetailsVisible]);

  return (
    <Stack gap={ITEMS_GAP}>
      <div>
        {props.requireTOS ? (
          <>
            <CheckboxComponent
              checkedSetter={(event) => {
                props.terms.onChange(event.target.checked);
              }}
              isChecked={props.terms.accepted}
              text={<TermsAndPrivacyNoticeComponent />}
              textStyle={privacyTextStyle}
            />

            {props.terms.showError && (
              <FormErrorMessageComponent
                css={authorizationPagesErrorStyle}
                isSingleLine={false}
                messages={[t('signup.agreement.warning')]}
              />
            )}
          </>
        ) : (
          <TermsAndPrivacyNoticeComponent
            consentType="implicit"
            css={privacyNoticeStyle}
          />
        )}
      </div>

      <div>
        <SsoButtonsComponent
          isDisabled={props.isLoading}
          onGoogleLogin={props.sso.onGoogleLogin}
          onMicrosoftLogin={props.sso.onMicrosoftLogin}
        />
      </div>

      <div css={orDividerWrapperStyle}>
        <div css={orDividerLineStyle} />
        <div css={orDividerTextStyle}>{t('or')}</div>
        <div css={orDividerLineStyle} />
      </div>

      <div css={columnStyle}>
        <TextInputAuthComponent
          autoComplete="username"
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          label={t('Email')}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          onFocus={props.onEmailFocus}
          placeholder={t('Email')}
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            isSingleLine={false}
            messages={props.email.errors}
          />
        )}
      </div>

      {props.areDetailsVisible && (
        <div
          css={detailsWrapperStyle}
          onAnimationEnd={() => setIsExpandFinished(true)}
        >
          <div css={[detailsContentStyle, isExpandFinished && detailsContentExpandedStyle]}>
            <Stack
              css={rowWithColumnsStyle}
              gap={ITEMS_GAP}
            >
              <div css={columnStyle}>
                <TextInputAuthComponent
                  autoComplete="given-name"
                  hasError={props.firstName.errors.length > 0}
                  icon={faFaceGrinWide}
                  isDisabled={props.isLoading}
                  label={t('First Name')}
                  name="given-name"
                  onBlur={props.firstName.onBlur}
                  onChange={props.firstName.onChange}
                  placeholder={t('First Name')}
                  value={props.firstName.value}
                />

                {props.firstName.errors.length > 0 && (
                  <FormErrorMessageComponent
                    css={authorizationPagesErrorStyle}
                    isSingleLine={false}
                    messages={props.firstName.errors}
                  />
                )}
              </div>

              <div css={columnStyle}>
                <TextInputAuthComponent
                  autoComplete="family-name"
                  hasError={props.lastName.errors.length > 0}
                  icon={faFaceSunglasses}
                  isDisabled={props.isLoading}
                  label={t('Last Name')}
                  name="family-name"
                  onBlur={props.lastName.onBlur}
                  onChange={props.lastName.onChange}
                  placeholder={t('Last Name')}
                  value={props.lastName.value}
                />

                {props.lastName.errors.length > 0 && (
                  <FormErrorMessageComponent
                    css={authorizationPagesErrorStyle}
                    isSingleLine={false}
                    messages={props.lastName.errors}
                  />
                )}
              </div>
            </Stack>

            <Stack gap={8}>
              <div>
                <TextInputAuthComponent
                  autoComplete="new-password"
                  hasError={props.password.errors.length > 0}
                  icon={faLockAlt}
                  isDisabled={props.isLoading}
                  label={t('Password')}
                  name="password"
                  onBlur={props.password.onBlur}
                  onChange={props.password.onChange}
                  placeholder={t('Password')}
                  type="password"
                  value={props.password.value}
                />

                {props.password.errors.length > 0 && (
                  <FormErrorMessageComponent
                    css={authorizationPagesErrorStyle}
                    isSingleLine={false}
                    messages={props.password.errors}
                  />
                )}
              </div>

              <div css={passwordStrengthWrapperStyle}>
                <PasswordStrengthIndicatorComponent password={props.password.value} />
              </div>
            </Stack>
          </div>
        </div>
      )}

      <div>
        <AuthorizationSubmitButtonComponent
          isLoading={props.isLoading}
        >
          {t('Create Account')}
        </AuthorizationSubmitButtonComponent>
      </div>

      {props.apiError.message && !props.apiError.errors && (
        <AlertComponent
          css={alertStyle}
          onClose={props.apiError.onClose}
          stylingOptions={{ withoutBorder: true }}
          type="dangerAlternative"
        >
          {props.apiError.message}
        </AlertComponent>
      )}

      <RegisterFormFooter />
    </Stack>
  );
};
